/// <amd-module name="Test/TestFlaky/Components/TestFlakyDialog" />
import { TestFlakyActionCreator } from "Actions/TestFlakyActionCreator";
import * as React from "react";
import { defaultComparer, IconDetails } from "Test/common-util/Common.Utils";
import * as TCMContracts from "Test/common-util/Contracts/Test";
import { ITestResultTreeData } from "Test/common-util/Contracts/ViewContracts";
import * as Telemetry from "Test/common-util/Telemetry";
import * as TelemetryConstants from "Test/common-util/TelemetryConstants";
import { getFriendlyName, getIconDetails } from "Test/common-util/TestOutcomeUtils";
import { TestFlakyActionHub } from "Test/TestFlaky/Actions/TestFlakyActionHub";
import * as Resources from "Test/TestFlaky/Resources";
import { ITestFlakyState, TestFlakyStore } from "Test/TestFlaky/Store/TestFlakyStore";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { Components, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Dialog } from "VSSUI/Dialog";
import { Icon } from "VSSUI/Icon";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { Tooltip } from "VSSUI/TooltipEx";
import { ago } from "VSSUI/Utilities/Date";
import { ScreenSizeObserver } from "VSSUI/Utilities/ScreenSize";
import { ScreenSize } from "VSS/Core/Util/Screen";

export interface ITestFlakyDialogProps {
    testRunId: number;
    testResultId: number;
    isTestResultFlaky: boolean;
    flakyTestResult: ITestResultTreeData;
    onFlakyStateChange: () => void;
    testCaseRefId?: number;
}

export class TestFlakyDialog extends VssComponent<ITestFlakyDialogProps, ITestFlakyState> {
    private _store: TestFlakyStore;
    private _actionHub: TestFlakyActionHub;
    private _actionCreator: TestFlakyActionCreator;
    private _pageContext: IVssPageContext;

    public constructor(props: ITestFlakyDialogProps, context: IVssComponentContext) {
        super(props, context);
        this._pageContext = context.pageContext;
        this._actionHub = new TestFlakyActionHub();
        this._store = new TestFlakyStore(this._pageContext, this._actionHub, this.props.isTestResultFlaky);
        this._actionCreator = new TestFlakyActionCreator(this._pageContext, this._actionHub);
        this.state = this._store.getState();
    }

    public async componentDidMount(): Promise<void> {
        super.componentDidMount();
        this._store.addChangedListener(this._handleStoreChange);
        await this._actionCreator.initialize(this.props.testRunId, this.props.testResultId, this.props.testCaseRefId);
    }

    public componentWillUnmount(): void {
        this._store.dispose();
        this._store.removeChangedListener(this._handleStoreChange);
        super.componentWillUnmount();
    }


    public render(): JSX.Element {

        return (
            <ScreenSizeObserver>
                {
                    (screenSizeProps: { screenSize: ScreenSize }) => {
                        return this._renderCustom(screenSizeProps.screenSize);
                    }
                }
            </ScreenSizeObserver>
        );
    }

    private _renderCustom(screenSize: ScreenSize): JSX.Element {

        let calloutContentClassName: string = this._getCalloutClassname(screenSize);
        return (
            <div>
                {this.state.showDialog && (
                    <Dialog
                        className={calloutContentClassName}
                        titleProps={{ text: this.getTitleText() }}
                        footerButtonProps={[
                            {
                                text: Resources.CancelButtonText,
                                onClick: this._onDismiss
                            },
                            {
                                text: Resources.OkButtonText,
                                primary: true,
                                disabled: !!this.state.errorMessage,
                                onClick: this._onClickOk
                            }
                        ]}
                        onDismiss={this._onDismiss}
                    >
                        {this.state.loading && <Spinner className={"flaky-spinner"} size={SpinnerSize.medium} />}
                        {this.state.errorMessage && this.state.errorMessage !== "" && this._getErrorMessageElement()}
                        {!this.state.loading && !this.state.errorMessage && this.state.testResults && this.state.testRun && (
                            <div className={"flaky-content"}>
                                <span className={"content-container"}>
                                    {this.props.isTestResultFlaky ? Resources.UnflakyMessageBar : Resources.FlakyMessageBar}
                                    <p />
                                    {this._getRowField(Resources.TestName, this.state.testResults.testCaseTitle)}
                                    {this._getResultOutcomeField()}
                                    {this._getBranchField()}
                                    {this._getRowField(Resources.Container, this.state.testResults.container)}
                                </span>
                            </div>
                        )}
                    </Dialog>
                )}
            </div>
        );
    }


    private _getCalloutClassname(screenSize: ScreenSize): string {
        let calloutContentClassName: string = "flaky-dialog";

        if (screenSize < ScreenSize.small) {
            calloutContentClassName = calloutContentClassName + " flaky-dialog-sm";
        }
        return calloutContentClassName;
    }

    private _handleStoreChange = (): void => {
        this.setState(this._store.getState());
    };

    private _onClickOk = () => {
        Telemetry.publishEvent(this.context.pageContext, this._pageContext, TelemetryConstants.featureTestTab_MarkUnMarkFlakyClicked, {
            count: 1,
            MarkFlaky: this.props.isTestResultFlaky
        });

        this._actionCreator
            .updateFlakinessStatus(
                this.state.testResults.testCaseRefId,
                this.state.testRun.BranchName,
                !this.props.isTestResultFlaky,
                this.props.flakyTestResult
            )
            .then(() => {
                this.props.onFlakyStateChange();
            });
    };

    private _onDismiss = () => {
        Telemetry.publishEvent(this.context.pageContext, this._pageContext, TelemetryConstants.featureTestTab_FlakyCanceledClicked, {
            count: 1
        });
        this._actionHub.onDismiss.invoke(undefined);
    };

    private getTitleText(): string {
        if (this.state.loading) {
            return "";
        }

        return this.props.isTestResultFlaky ? Resources.UnmarkTestCaseFlaky : Resources.MarkTestCaseFlaky;
    }

    private _getRowField(fieldName: string, fieldValue: string): JSX.Element {
        return (
            <div className="flex-row field-row">
                <label className="field-name">{fieldName}:</label>
                <Tooltip text={fieldValue}>
                    <div className="field-value-container">
                        <label className="field-value">{fieldValue}</label>
                    </div>
                </Tooltip>
            </div>
        );
    }

    private _getBranchField(): JSX.Element {
        const branchLabel: string = this.state.isPRBuild ? Resources.TargetBranch : Resources.SourceBranch;

        return (
            <div className="flex-row field-row">
                <label className="field-name">{branchLabel}:</label>
                <Tooltip text={this.state.testRun.BranchName}>
                    <div className="field-value-container">
                        <label className="field-value">{this.state.testRun.BranchName}</label>
                    </div>
                </Tooltip>
            </div>
        );
    }

    private _getResultOutcomeField(): JSX.Element {
        return (
            <div className="flex-row field-row">
                <label className="field-name">{Resources.Outcome}:</label>
                <div className="field-value-container">
                    <label className="field-value flex-row">
                        {this._getOutcomeIcon()}
                        <span className="flaky-result-outcome-status-computerName">{this._getOutcomeAndComputerNameInfoText()}</span>
                    </label>
                </div>
            </div>
        );
    }

    private _getOutcomeIcon(): JSX.Element {
        let iconDetails: IconDetails;
        if (this.state.testResults.outcome) {
            iconDetails = getIconDetails(this.state.testResults.outcome);
        } else {
            iconDetails = getIconDetails(TCMContracts.TestOutcome.None);
        }
        return <Icon iconName={iconDetails.iconName} className={`${iconDetails.className} flaky-testresult-outcome-icon flacky-icon-prop`} />;
    }

    private _getOutcomeAndComputerNameInfoText(): JSX.Element {
        const outcome: string = this.state.testResults.outcome ? getFriendlyName(this.state.testResults.outcome) : "";
        const completedSince: string = this._calculateCompletedSince();
        return this.state.testResults.computerName ? (
            <span className="flaky-test-results-outcome-machine-name-info">
                {format(Resources.ResultOutcomeAndMachineNameInfo, outcome, completedSince, this.state.testResults.computerName)}
            </span>
        ) : (
                <span className="flaky-test-result-outcome-info">{format(Resources.ResultOutcomeInfo, outcome, completedSince)}</span>
            );
    }

    private _calculateCompletedSince(): string {
        let sb = "";

        if (defaultComparer(this.state.testResults.state, "Completed") === 0 && this.state.testResults.completedDate) {
            sb = sb + " ";

            // date will be in client time zone. ago will compare date in client time zone.
            sb = sb + ago(this.state.testResults.completedDate);
        } else if (this.state.testResults.startedDate) {
            sb = sb + " ";

            // date will be in client time zone. ago will compare date in client time zone.
            sb = sb + ago(this.state.testResults.startedDate);
        }

        return sb.toString();
    }

    private _getErrorMessageElement(): JSX.Element {
        return (
            <div className="flaky-message-error">
                <MessageCard severity={MessageCardSeverity.Error}>
                    <div className="flaky-errormessage-bar">{this.state.errorMessage}</div>
                </MessageCard>
            </div>
        );
    }
}

Components.add("test-testflaky", TestFlakyDialog);
